import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown } from "lucide-react";
import { getLeistungBySlug } from "@/data/leistungen";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-hb-border">
      <button
        className="w-full flex items-center justify-between py-5 text-left group"
        onClick={() => setOpen(!open)}
        data-testid="faq-item-btn"
      >
        <span className="text-sm font-semibold text-hb-dark font-manrope pr-4">{q}</span>
        <ChevronDown
          size={16}
          className={`text-hb-muted flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="pb-5">
          <p className="text-sm text-hb-text font-inter leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function LeistungDetail() {
  const { slug } = useParams();
  const leistung = getLeistungBySlug(slug);
  const { lang } = useLanguage();
  useReveal();

  const t = (de, it) => lang === 'it' ? it : de;
  const d = (field) => (lang === 'it' && leistung.it?.[field]) ? leistung.it[field] : leistung[field];

  if (!leistung) {
    return (
      <div className="pt-40 text-center">
        <p className="text-hb-muted"><T de="Leistung nicht gefunden." it="Servizio non trovato." /></p>
        <Link to="/leistungen" className="btn-link mt-4 inline-block">
          <T de="Zurück zu Leistungen" it="Torna ai Servizi" />
        </Link>
      </div>
    );
  }

  const title = d('title');
  const subtitle = d('subtitle');
  const intro = d('intro');
  const whyWood = d('whyWood');
  const process = d('process');
  const useCases = d('useCases');
  const faq = d('faq');

  return (
    <div className="pt-20 md:pt-24">
      {/* Hero */}
      <section
        data-testid="leistung-hero"
        className="relative min-h-[60vh] flex items-end pb-16 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={leistung.heroImage}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-hb-dark/80 via-hb-dark/30 to-transparent" />
        </div>
        <div className="relative z-10 container-hb">
          <Link
            to="/leistungen"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs tracking-widest uppercase font-semibold mb-8 transition-colors"
          >
            <ArrowLeft size={12} /> <T de="Leistungen" it="Servizi" />
          </Link>
          <h1
            data-testid="leistung-title"
            className="text-5xl sm:text-6xl font-manrope font-extrabold text-white leading-tight mb-3"
          >
            {title}
          </h1>
          <p className="text-xl text-hb-wood font-inter">{subtitle}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="section-gap bg-white">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-3">
              <span className="label-text text-hb-wood block mb-5">
                <T de="Überblick" it="Panoramica" />
              </span>
              <p className="text-xl text-hb-dark font-inter leading-relaxed mb-6 font-light">
                {intro}
              </p>
              <Link
                to="/kontakt"
                data-testid="leistung-cta-btn"
                className="btn-primary"
              >
                <T de="Projekt besprechen" it="Discutere il progetto" />
              </Link>
            </div>
            <div className="lg:col-span-2 bg-hb-off-white p-8">
              <h3 className="text-xs tracking-widest uppercase text-hb-muted font-semibold mb-5">
                <T de="Typische Projekte" it="Progetti tipici" />
              </h3>
              <ul className="space-y-3">
                {useCases.map((uc, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-hb-text font-inter">
                    <Check size={14} className="text-hb-wood flex-shrink-0" />
                    {uc}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      {leistung.photos && leistung.photos.length > 0 && (
        <section className="overflow-hidden" style={{ background: "#0b0c0e" }}>
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: "2px" }}>
            {leistung.photos.map((photo, i) => (
              <div
                key={i}
                className="relative overflow-hidden group"
                style={{ height: "clamp(220px, 28vw, 380px)" }}
              >
                <img
                  src={photo.src}
                  alt={photo.caption}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{ objectPosition: "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-[10px] tracking-widest uppercase font-inter font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>
                    {photo.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Warum Holz */}
      <section className="section-gap bg-hb-off-white" data-testid="why-wood-section">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-5">
            <T de="Warum Holz" it="Perché il legno" />
          </span>
          <h2 className="section-title mb-14 max-w-lg">
            <T de="Die Argumente." it="Le ragioni." />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-hb-border">
            {whyWood.map((w, i) => (
              <div key={i} className="bg-white p-8">
                <span className="font-mono text-xs text-hb-wood/60 block mb-3">0{i + 1}</span>
                <h3 className="font-manrope font-bold text-hb-dark mb-3">{w.title}</h3>
                <p className="text-sm text-hb-text font-inter leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prozess */}
      <section className="section-gap bg-white" data-testid="process-section">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-5">
            <T de="Ablauf" it="Procedura" />
          </span>
          <h2 className="section-title mb-14 max-w-lg">
            <T de="Wie wir arbeiten." it="Come lavoriamo." />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-hb-border">
            {process.map((step, i) => (
              <div key={i} className="bg-white p-8 group">
                <span className="font-mono text-2xl font-bold text-hb-wood/30 block mb-4">
                  {step.step}
                </span>
                <h3 className="font-manrope font-bold text-hb-dark mb-3">{step.title}</h3>
                <p className="text-sm text-hb-text font-inter leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq && (
        <section className="section-gap bg-hb-off-white" data-testid="faq-section">
          <div className="container-hb">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <span className="label-text text-hb-wood block mb-5">FAQ</span>
                <h2 className="section-title">
                  <T de="Häufige Fragen." it="Domande frequenti." />
                </h2>
              </div>
              <div>
                {faq.map((item, i) => (
                  <FaqItem key={i} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-hb-dark" data-testid="leistung-bottom-cta">
        <div className="container-hb text-center">
          <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-white mb-4">
            <T de="Bereit für Ihr Projekt?" it="Pronti per il vostro progetto?" />
          </h2>
          <p className="text-base text-white/60 font-inter mb-8 max-w-md mx-auto">
            <T de="Wir beraten Sie persönlich und erstellen ein unverbindliches Angebot."
               it="Vi consigliamo personalmente e prepariamo un preventivo senza impegno." />
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="leistung-final-cta"
              className="btn-primary bg-white text-hb-dark hover:bg-hb-off-white"
            >
              <T de="Anfrage stellen" it="Richiedi un preventivo" />
            </Link>
            <a
              href="tel:+390473238030"
              className="btn-secondary border-white/30 text-white hover:border-white/60"
            >
              +39 0473 238 030
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
